import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginService } from '@ebw-monorepo/login';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import {
  AnteaCredentials,
  AnteaElaborazione,
  AnteaToken,
  ControlloDaTrasmettere,
  EsitoTrasmissioneSuperamento,
  SuperamentoDaTrasmettere
} from '../models/antea.model';

/**
 * Servizio per l'autenticazione verso il portale AnTeA (via proxy Kode) e per
 * condividere il token ottenuto tra i sotto-componenti di "Trasmissione AnTeA".
 */
@Injectable({
  providedIn: 'root'
})
export class AnteaService {
  private readonly _token$ = new BehaviorSubject<string | null>(null);
  public readonly token$: Observable<string | null> = this._token$.asObservable();

  constructor(
    private _http: HttpClient,
    private _loginService: LoginService,
    private _apiUrlService: ApiUrlService
  ) { }

  get _headers(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Bearer ' + this._loginService.getToken()
    });
  }

  // Header richiesto da trasmetti-controlli/elaborazione: il token AnTeA ottenuto dal login
  get _anteaHeaders(): HttpHeaders {
    return this._headers.set('X-Antea-Token', this.token ?? '');
  }

  public get token(): string | null {
    return this._token$.value;
  }

  public clearToken(): void {
    this._token$.next(null);
  }

  // ------------------------------------------------------------------------------------------------

  public login(credentials: AnteaCredentials): Observable<AnteaToken> {
    const url = this._apiUrlService.createUrl(API_URLS.ANTEA.LOGIN);
    const headers = { headers: this._headers };
    return this._http.post<AnteaToken>(url, credentials, headers).pipe(tap((token) => this._token$.next(token.access_token)));
  }

  // ------------------------------------------------------------------------------------------------

  public getWsz(): Observable<string[]> {
    const url = this._apiUrlService.createUrl(API_URLS.ANTEA.WSZ);
    return this._http.get<string[]>(url, { headers: this._headers });
  }

  // ------------------------------------------------------------------------------------------------

  public getControlliDaTrasmettere(wsz: string): Observable<ControlloDaTrasmettere[]> {
    const url = `${this._apiUrlService.createUrl(API_URLS.ANTEA.WSZ)}/${wsz}/controlli-da-trasmettere`;
    return this._http.get<ControlloDaTrasmettere[]>(url, { headers: this._headers });
  }

  // ------------------------------------------------------------------------------------------------

  public trasmettiControlli(prelievoIds: number[]): Observable<AnteaElaborazione> {
    const url = this._apiUrlService.createUrl(API_URLS.ANTEA.TRASMETTI_CONTROLLI);
    return this._http.post<AnteaElaborazione>(url, { prelievoIds }, { headers: this._anteaHeaders });
  }

  // ------------------------------------------------------------------------------------------------

  public getElaborazione(idElaborazione: string): Observable<AnteaElaborazione> {
    const url = `${this._apiUrlService.createUrl(API_URLS.ANTEA.ELABORAZIONE)}/${idElaborazione}`;
    return this._http.get<AnteaElaborazione>(url, { headers: this._anteaHeaders });
  }

  // ------------------------------------------------------------------------------------------------

  public getSuperamentiDaTrasmettere(wsz: string): Observable<SuperamentoDaTrasmettere[]> {
    const url = `${this._apiUrlService.createUrl(API_URLS.ANTEA.WSZ)}/${wsz}/superamenti-da-trasmettere`;
    return this._http.get<SuperamentoDaTrasmettere[]>(url, { headers: this._headers });
  }

  // ------------------------------------------------------------------------------------------------

  public trasmettiSuperamenti(superamentoIds: number[]): Observable<EsitoTrasmissioneSuperamento[]> {
    const url = this._apiUrlService.createUrl(API_URLS.ANTEA.TRASMETTI_SUPERAMENTI);
    return this._http.post<EsitoTrasmissioneSuperamento[]>(url, { superamentoIds }, { headers: this._anteaHeaders });
  }
}
